<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { IconCompare } from '@/components/design/icons';
import { VaultFileRevisionKind, type VaultFileRevisionInDb } from '@/api';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import {
  gaiaAccent,
  gaiaDiff,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaTracking,
} from '@/components/design/themes/tokens';

const props = defineProps<{
  show: boolean;
  fileUid: string;
}>();

const emit = defineEmits<{
  'update:show': [boolean];
  compare: [{ revisionA: VaultFileRevisionInDb; revisionB: VaultFileRevisionInDb }];
}>();

const vaultStore = useVaultStore();

const selectedA = ref<string | null>(null);
const selectedB = ref<string | null>(null);
const revisions = ref<VaultFileRevisionInDb[]>([]);
const totalCount = ref(-1);
const loadingRevisions = ref(false);
const PAGE_SIZE = 30;

const isFinished = computed(
  () => totalCount.value >= 0 && revisions.value.length >= totalCount.value
);

async function loadMore(): Promise<void> {
  if (isFinished.value || loadingRevisions.value) return;
  loadingRevisions.value = true;
  try {
    const res = await vaultStore.listRevisions(props.fileUid, PAGE_SIZE, revisions.value.length);
    revisions.value.push(...res.results);
    totalCount.value = res.result_count;
  } finally {
    loadingRevisions.value = false;
  }
}

watch(
  () => props.show,
  val => {
    if (val) {
      revisions.value = [];
      totalCount.value = -1;
      selectedA.value = null;
      selectedB.value = null;
      void loadMore();
    }
  }
);

const selectedBRevision = computed(
  () => revisions.value.find(r => r.uid === selectedB.value) ?? null
);
const selectedARevision = computed(
  () => revisions.value.find(r => r.uid === selectedA.value) ?? null
);

const optionsA = computed(() =>
  revisions.value.map(r => ({
    label: `v${r.version_number} — ${r.file_name}`,
    value: r.uid,
    // A draft version stores no text, so it would compare as empty.
    disabled:
      r.kind === VaultFileRevisionKind.TEMPLATED ||
      (selectedBRevision.value
        ? r.version_number >= selectedBRevision.value.version_number
        : false),
  }))
);

const optionsB = computed(() =>
  revisions.value.map(r => ({
    label: `v${r.version_number} — ${r.file_name}`,
    value: r.uid,
    disabled:
      r.kind === VaultFileRevisionKind.TEMPLATED ||
      (selectedARevision.value
        ? r.version_number <= selectedARevision.value.version_number
        : false),
  }))
);

watch(selectedA, () => {
  if (
    selectedARevision.value &&
    selectedBRevision.value &&
    selectedARevision.value.version_number >= selectedBRevision.value.version_number
  ) {
    selectedB.value = null;
  }
});

watch(selectedB, () => {
  if (
    selectedARevision.value &&
    selectedBRevision.value &&
    selectedBRevision.value.version_number <= selectedARevision.value.version_number
  ) {
    selectedA.value = null;
  }
});

const labelA = computed(() => {
  const r = revisions.value.find(x => x.uid === selectedA.value);
  return r ? `v${r.version_number}` : null;
});

const labelB = computed(() => {
  const r = revisions.value.find(x => x.uid === selectedB.value);
  return r ? `v${r.version_number}` : null;
});

const canCompare = computed(
  () => selectedA.value && selectedB.value && selectedA.value !== selectedB.value
);

function handleCompare(): void {
  if (!canCompare.value) return;
  const revA = revisions.value.find(r => r.uid === selectedA.value);
  const revB = revisions.value.find(r => r.uid === selectedB.value);
  if (!revA || !revB) return;
  emit('compare', { revisionA: revA, revisionB: revB });
  emit('update:show', false);
}

function handleClose(): void {
  emit('update:show', false);
}

const accentColor = gaiaAccent.base;
const faintColor = gaiaNeutral.textFaint;
/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--compare-added-badge': gaiaDiff.addedBadgeSurface,
  '--compare-added-strong': gaiaDiff.addedStrong,
  '--compare-mark-size': gaiaFontSize.nano,
  '--compare-mark-weight': String(gaiaFontWeight.bold),
  '--compare-mark-tracking': gaiaTracking.labelSoft,
  '--compare-mono-font': gaiaFonts.mono,
  '--compare-removed-badge': gaiaDiff.removedBadgeSurface,
  '--compare-removed-strong': gaiaDiff.removedStrong,
};
</script>

<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="480px"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <n-flex align="center" :size="10" :wrap="false">
        <n-icon :component="IconCompare" :size="17" :color="accentColor" />
        <n-flex vertical :size="2">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Compare versions</gaia-text>
          <gaia-text size="meta" tone="subtle">Pick two revisions to see what changed</gaia-text>
        </n-flex>
      </n-flex>
    </template>

    <!-- `compare-modal-body` is a tour anchor (tours/vault.ts stamps its
         ancestor card) — keep the class if this markup is reworked. -->
    <n-flex vertical :size="16" class="compare-modal-body">
      <gaia-sub-panel>
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text size="control" tone="tertiary">Comparing</gaia-text>
          <span class="version older">{{ labelA || '—' }}</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            :style="{ color: faintColor, flex: 'none' }"
          >
            <path d="M3 8h10M9.5 4.5 13 8l-3.5 3.5" />
          </svg>
          <span class="version newer">{{ labelB || '—' }}</span>
        </n-flex>
      </gaia-sub-panel>

      <gaia-form-field label="Version A">
        <template #action>
          <span class="badge older">older</span>
        </template>
        <gaia-remote-select
          v-model:value="selectedA"
          :options="optionsA"
          :loading="loadingRevisions"
          filterable
          placeholder="Select the earlier version"
          :style="{ width: '100%' }"
          :remote="false"
          @load-more="loadMore"
        />
      </gaia-form-field>

      <gaia-form-field label="Version B">
        <template #action>
          <span class="badge newer">newer</span>
        </template>
        <gaia-remote-select
          v-model:value="selectedB"
          :options="optionsB"
          :loading="loadingRevisions"
          filterable
          placeholder="Select the later version"
          :style="{ width: '100%' }"
          :remote="false"
          @load-more="loadMore"
        />
      </gaia-form-field>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" @click="handleClose">Cancel</gaia-button>
        <gaia-button type="primary" size="small" :disabled="!canCompare" @click="handleCompare">
          <template #icon>
            <n-icon :component="IconCompare" />
          </template>
          Compare
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* The two version marks, in the same tones the diff and the PDF marks use. */
.version {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 20px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: 10px;
  font-family: var(--compare-mono-font);
  font-size: var(--compare-mark-size);
  font-weight: var(--compare-mark-weight);
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: var(--compare-mark-size);
  font-weight: var(--compare-mark-weight);
  text-transform: uppercase;
  letter-spacing: var(--compare-mark-tracking);
}

.version.older,
.badge.older {
  border-color: var(--compare-removed-strong);
  background: var(--compare-removed-badge);
  color: var(--compare-removed-strong);
}

.version.newer,
.badge.newer {
  border-color: var(--compare-added-strong);
  background: var(--compare-added-badge);
  color: var(--compare-added-strong);
}
</style>
